import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FileText, Briefcase, Shield, BookOpen, Users, Building2, TrendingUp, DollarSign, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Services = () => {
  const services = [
    {
      icon: FileText,
      title: 'Personal Tax',
      description: 'Complete personal tax filing services tailored for Canadian residents.',
      features: [
        'Personal Income Tax (T1)',
        'Student Tax Returns',
        'Self-Employed Tax Returns',
        'Foreign Income Reporting',
        'Newcomer Tax Returns',
        'Personal GST/HST Returns',
        'Tax Amendments (T1 Adjustment)',
      ],
    },
    {
      icon: Briefcase,
      title: 'Business Tax',
      description: 'Comprehensive business tax services for corporations and businesses.',
      features: [
        'Corporate Tax Returns (T2)',
        'GST/HST Returns',
        'Payroll Tax & T4 Filing',
        'Tax Planning',
        'Year-End Tax Preparation',
      ],
    },
    {
      icon: Shield,
      title: 'CRA Support',
      description: 'Professional representation and assistance for all CRA-related matters.',
      features: [
        'CRA Audit Assistance',
        'CRA Reviews',
        'Child Benefit Application',
        'Notice of Assessment Review',
        'Tax Dispute Support',
        'Payment Arrangement Assistance',
      ],
    },
    {
      icon: BookOpen,
      title: 'Accounting & Bookkeeping',
      description: 'Reliable bookkeeping and accounting services to keep your business organized.',
      features: [
        'Monthly Bookkeeping and Reporting',
        'Bank Reconciliation',
        'AR and AP Reconciliation',
        'Journal Entries',
        'Year-End Adjustments',
        'Financial Statement Preparation',
      ],
    },
    {
      icon: Users,
      title: 'Payroll Services',
      description: 'Complete payroll management and remittance services for your business.',
      features: [
        'Payroll Processing',
        'Payroll Account Registration',
        'Direct Deposit Setup',
        'Employee Payslips',
        'T4 and T4A Preparation',
        'ROE Preparation',
        'Payroll Remittances',
        'WSIB Support',
      ],
    },
    {
      icon: Building2,
      title: 'Business Setup & CRA Registration',
      description: 'Start your business right with proper setup and CRA registrations.',
      features: [
        'Corporation Incorporation',
        'Sole Proprietorship Registration',
        'Business Name Registration',
        'Extra Provincial Registration',
        'Business Number (BN)',
        'GST/HST Account',
        'Payroll Account',
        'Import/Export Account',
      ],
    },
    {
      icon: TrendingUp,
      title: 'Business Advisory',
      description: 'Strategic business consulting to help you grow and succeed.',
      features: [
        'Business Consulting',
        'Tax Saving Strategies',
        'Business Growth Planning',
        'Cash Flow Management',
        'Pricing & Profitability Analysis',
        'Startup Advisory',
        'Financial Health Check',
      ],
    },
    {
      icon: DollarSign,
      title: 'Financing Support',
      description: 'Expert guidance for securing funding and financial support for your business.',
      features: [
        'SR&ED and Government Grants',
        'Start-up Loans',
        'Bank Loan / Line Support',
        'Mortgage Support',
        'Business Plan',
        'Business Loan Preparation',
        'Financial Projections',
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-32 pb-16 bg-gradient-to-br from-[#E0F2FE] via-white to-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-[#0F172A] mb-6">
              Our Services
            </h1>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Comprehensive tax, accounting, and business services tailored for Canadians
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={index}
                  data-testid={`service-card-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow"
                >
                  <div className="w-16 h-16 bg-[#E0F2FE] rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-8 h-8 text-[#005782]" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <ul className="space-y-3">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#005782] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Need Help with Any of These Services?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Contact us today for a free consultation and let's discuss how we can help
          </p>
          <Link to="/contact">
            <Button
              data-testid="services-cta-btn"
              className="bg-white text-[#005782] hover:bg-slate-100 rounded-lg px-8 py-4 text-lg font-medium shadow-lg"
            >
              Schedule Consultation
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
